/**
 * Owner portal · one apartment, read-only.
 *
 * The apartment's rooms, rent and occupancy. Its documents are not repeated
 * here: every room and apartment document lives in the property's Documents
 * tab, with its level and tags, and is uploaded from there (Neel, 20 Oct).
 */
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOwnerPropertyAccess } from "@/hooks/v2/useOwnerPropertyAccess";
import { useOwnerApartmentDetail } from "@/hooks/owner/useOwnerPropertyDetail";
import { V2AdminNavbar } from "@/components/admin/v2/V2AdminNavbar";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronRight, DoorClosed } from "lucide-react";
import { cn } from "@/lib/utils";
import { PAGE_TITLE_CLASS } from "@/components/admin/v2/page-title";

function formatEuro(amount: number | null | undefined, locale: string) {
  if (amount == null) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function OwnerApartmentDetail() {
  const { apartmentId } = useParams<{ apartmentId: string }>();
  const id = apartmentId ? Number(apartmentId) : null;
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const { isOwnerPortalOnly } = useAuth();

  const { data, isLoading, isError, error } = useOwnerApartmentDetail(id);
  const apartment = data?.apartment ?? null;
  const rooms = data?.rooms ?? [];

  const access = useOwnerPropertyAccess(
    isOwnerPortalOnly ? (apartment?.property_id ?? null) : null
  );

  if (isOwnerPortalOnly && access.data === false) {
    return <Navigate to="/admin?tab=analytics" replace />;
  }

  const notFound = !isLoading && !isError && data === null;

  return (
    <div className="min-h-screen bg-background">
      <V2AdminNavbar />
      <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 lg:px-8 pt-6 pb-12 space-y-6">
        <nav
          aria-label={t("owner.breadcrumbAriaLabel", "Breadcrumb")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to="/admin?tab=analytics"
            className="hover:text-foreground transition-colors"
          >
            {t("admin.nav.ownerAnalytics", "Analytics")}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          {apartment?.property_id ? (
            <Link
              to={`/admin/owner/property/${apartment.property_id}`}
              className="hover:text-foreground transition-colors truncate"
            >
              {apartment.property_title ??
                t("owner.detail.property", "Property")}
            </Link>
          ) : null}
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground font-medium truncate">
            {apartment?.label ?? t("owner.detail.apartment", "Apartment")}
          </span>
        </nav>

        {isError ? (
          <Alert variant="destructive" className="rounded-xl">
            <AlertTitle>
              {t("owner.apartment.loadErrorTitle", "Could not load apartment")}
            </AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : t(
                    "owner.apartment.loadErrorHint",
                    "Apply the latest Supabase migrations so the owner_apartment_detail function exists, then retry."
                  )}
            </AlertDescription>
          </Alert>
        ) : null}

        {notFound ? (
          <Alert className="rounded-xl">
            <AlertTitle>
              {t("owner.apartment.notFoundTitle", "Apartment unavailable")}
            </AlertTitle>
            <AlertDescription>
              {t(
                "owner.apartment.notFoundHint",
                "This apartment may have been removed, or it is in a property that is not assigned to your account."
              )}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {isLoading ? (
              <Skeleton className="h-8 w-64 rounded-lg" />
            ) : (
              <h1 className={PAGE_TITLE_CLASS}>
                {apartment?.label ?? t("owner.detail.apartment", "Apartment")}
              </h1>
            )}
          </div>
          {apartment ? (
            <p className="text-sm text-muted-foreground">
              {[
                apartment.floor,
                apartment.size_sqm != null ? `${apartment.size_sqm} m²` : null,
                apartment.num_rooms != null
                  ? `${apartment.num_rooms} ${t("owner.card.rooms", "Rooms")}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          ) : null}
        </div>

        <div>
          <PanelCard
            title={t("admin.section.rooms", "Rooms")}
            icon={<DoorClosed className="h-4 w-4" />}
          >
            {isLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full rounded" />
                ))}
              </div>
            ) : rooms.length === 0 ? (
              <p className="px-5 pb-6 pt-1 text-sm text-muted-foreground">
                {t(
                  "owner.apartment.noRooms",
                  "No rooms in this apartment yet."
                )}
              </p>
            ) : (
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>{t("owner.detail.colRoom", "Room")}</TableHead>
                      <TableHead>{t("owner.detail.colRent", "Rent")}</TableHead>
                      <TableHead className="text-right">
                        {t("owner.detail.colStatus", "Status")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rooms.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">
                          <Link
                            to={`/admin/owner/room/${r.id}`}
                            className="text-teal-700 hover:underline"
                          >
                            {r.title ?? t("owner.detail.room", "Room")}
                          </Link>
                          {r.floor || r.size_sqm != null ? (
                            <span className="block text-xs font-normal text-muted-foreground">
                              {[
                                r.floor,
                                r.size_sqm != null ? `${r.size_sqm} m²` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {formatEuro(r.monthly_rent, locale)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                              r.occupied
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            )}
                          >
                            {r.occupied
                              ? t("owner.detail.occupied", "Occupied")
                              : t("owner.detail.vacant", "Vacant")}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </PanelCard>
        </div>
      </div>
    </div>
  );
}

function PanelCard({
  title,
  icon,
  className,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      className={cn(
        "rounded-2xl border-border/60 shadow-sm overflow-hidden",
        className
      )}
    >
      <div className="flex items-center gap-2 px-5 pt-5 pb-3">
        {icon ? <span className="text-muted-foreground">{icon}</span> : null}
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      </div>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

export default OwnerApartmentDetail;
